import React, { useEffect, useState, lazy, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowDown, Check, Shield, Lock, Star, AlertTriangle, BookOpen, Brain, Eye, Users, X, ChevronDown, ChevronUp, Mail, Smartphone, RefreshCw, Infinity, Gift } from 'lucide-react';
import { UrgencyBar } from '../components/UrgencyBar';
import { PurchasePopup } from '../components/PurchasePopup';
import { TestimonialCard } from '../components/TestimonialCard';
// --- DATA ---
const BENEFITS = [{
  icon: <Eye className="w-6 h-6 text-yellow-500" data-id="element-59" />,
  title: 'Detectar mentiras al instante'
}, {
  icon: <Brain className="w-6 h-6 text-yellow-500" data-id="element-60" />,
  title: 'Leer intenciones ocultas'
}, {
  icon: <Users className="w-6 h-6 text-yellow-500" data-id="element-61" />,
  title: 'Dominar dinámicas de grupo'
}, {
  icon: <Shield className="w-6 h-6 text-yellow-500" data-id="element-62" />,
  title: 'Blindarte contra manipulación'
}, {
  icon: <Star className="w-6 h-6 text-yellow-500" data-id="element-63" />,
  title: 'Aumentar tu estatus percibido'
}, {
  icon: <Lock className="w-6 h-6 text-yellow-500" data-id="element-64" />,
  title: 'Negociar con ventaja injusta'
}];
const BONUSES = [{
  title: 'BONUS #1: Manual de Microexpresiones',
  subtitle: 'Lo que la cara no puede ocultar',
  description: 'Aprendé a detectar miedo, culpa, tensión reprimida y señales que duran menos de un segundo.'
}, {
  title: 'BONUS #2: Control de Conversaciones',
  subtitle: 'Dominá el marco verbal',
  description: 'Redirigí preguntas, evitá quedar en posición débil y ganá discusiones sin elevar la voz.'
}, {
  title: 'BONUS #3: Defensa Anti-Manipulación',
  subtitle: 'Escudo psicológico total',
  description: 'Gaslighting, victimización y presión social. Aprendé a neutralizar sin confrontaciones.'
}];
const TESTIMONIALS = [{
  name: 'Javier M.',
  role: 'Gerente de Ventas, 34 años',
  content: 'Siempre sentía que en las reuniones me perdía de algo. Con el módulo de jerarquías ocultas entendí quién realmente tomaba las decisiones. Cerré dos tratos grandes la semana pasada aplicando esto.'
}, {
  name: 'Sofía L.',
  role: 'Abogada, 29 años',
  content: 'La sección de microexpresiones es brutal. Pude detectar una mentira clave en una negociación extrajudicial solo observando la tensión en la mandíbula del oponente. Se pagó solo.'
}, {
  name: 'Marcos D.',
  role: 'Emprendedor, 41 años',
  content: 'No es motivación barata. Es un sistema técnico. Lo uso para contratar personal y para negociar con proveedores. La ventaja que te da saber lo que el otro piensa es injusta.'
}, {
  name: 'Valentina R.',
  role: 'Estudiante de Psicología, 22 años',
  content: 'En la facultad te enseñan teoría. Esto es práctica pura. Los códigos de comportamiento en grupos me cambiaron la forma de ver mis relaciones sociales.'
}, {
  name: 'Esteban K.',
  role: 'Team Leader, 38 años',
  content: 'El manual de influencia aplicada es oro. Logré que mi equipo adoptara un cambio difícil sin resistencia, usando solo los principios de autoridad percibida.'
}];
const FAQS = [{
  q: '¿Cómo recibo el material?',
  a: 'Inmediatamente después del pago recibís un email con los links de descarga. Todo es digital en PDF.'
}, {
  q: '¿Funciona para cualquier persona?',
  a: 'Sí. El sistema está diseñado para cualquier persona que interactúe con otros: profesionales, emprendedores, estudiantes, líderes.'
}, {
  q: '¿Es contenido teórico o práctico?',
  a: 'Es un sistema 100% aplicable. Cada módulo incluye ejemplos reales y ejercicios de observación.'
}, {
  q: '¿Qué pasa si no me gusta?',
  a: 'Tenés 7 días para reportar cualquier inconveniente con la descarga o el acceso. Te lo resolvemos o te devolvemos el dinero.'
}, {
  q: '¿El pago es seguro?',
  a: 'Sí. Procesado por MercadoPago con encriptación SSL. Tus datos están 100% protegidos.'
}, {
  q: '¿Puedo leerlo en el celular?',
  a: 'Sí. Los PDFs son compatibles con cualquier dispositivo.'
}];
// COMBO_DATA without onBuy — handlers are attached inside the component
const COMBO_DATA = [{
  title: 'Combo 2: El Más Elegido',
  price: '29.990',
  originalPrice: '55.000',
  discount: '55%',
  description: 'El equilibrio perfecto entre detección e influencia.',
  isRecommended: true,
  features: [{
    text: 'Manual Principal: Códigos Ocultos (60 módulos)'
  }, {
    text: 'Manual 2: Influencia Psicológica Aplicada',
    highlight: true
  }, {
    text: 'Bonus: Microexpresiones Reales',
    bonus: true
  }, {
    text: 'Bonus: Control de Conversaciones',
    bonus: true
  }, {
    text: 'Bonus: Defensa Contra Manipulación',
    bonus: true
  }, {
    text: '📄 Material digital (PDF) — enviado por mail'
  }],
  link: 'https://checkout7.rosetadigital.com/cart/46233866698923:1',
  comboName: 'Combo 2 - El Más Elegido',
  comboPrice: 29990
}, {
  title: 'Combo 1: Sistema Base',
  price: '19.990',
  originalPrice: '35.000',
  discount: '45%',
  description: 'Para iniciarse en la lectura de comportamiento.',
  features: [{
    text: 'Manual Principal: Códigos Ocultos (60 módulos)',
    highlight: true
  }, {
    text: 'Bonus: Microexpresiones Reales',
    bonus: true
  }, {
    text: 'Bonus: Control de Conversaciones',
    bonus: true
  }, {
    text: 'Bonus: Defensa Contra Manipulación',
    bonus: true
  }, {
    text: '📄 Material digital (PDF) — enviado por mail'
  }],
  link: 'https://checkout7.rosetadigital.com/cart/46233839665323:1',
  comboName: 'Combo 1 - Sistema Base',
  comboPrice: 19990
}, {
  title: 'Combo 3: Nivel Avanzado',
  price: '39.990',
  originalPrice: '80.000',
  discount: '60%',
  description: 'Dominio total de la estrategia social.',
  isPremium: true,
  features: [{
    text: 'Manual Principal: Códigos Ocultos (60 módulos)'
  }, {
    text: 'Manual 2: Influencia Psicológica Aplicada'
  }, {
    text: 'Manual 3: Estrategia Social Avanzada',
    highlight: true
  }, {
    text: 'Bonus: Microexpresiones Reales',
    bonus: true
  }, {
    text: 'Bonus: Control de Conversaciones',
    bonus: true
  }, {
    text: 'Bonus: Defensa Contra Manipulación',
    bonus: true
  }, {
    text: '📄 Material digital (PDF) — enviado por mail'
  }],
  link: 'https://checkout7.rosetadigital.com/cart/46233874071723:1',
  comboName: 'Combo 3 - Nivel Avanzado',
  comboPrice: 39990
}];
export function LandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [selectedCombo, setSelectedCombo] = useState(0); // Default to Combo 2 (now first)
  useEffect(() => {
    document.title = 'Códigos Ocultos';
    // OG meta tags for link previews
    const setMeta = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('og:title', 'Códigos Ocultos');
    setMeta('og:description', 'El sistema estructurado para leer intenciones, detectar mentiras y blindarte contra la manipulación moderna.');
    setMeta('og:image', 'https://cdn.magicpatterns.com/uploads/uSmcroKTKZCXej782ZGJuq/ChatGPT_Image_23_feb_2026,_17_24_01_(1).png');
    setMeta('og:type', 'website');
  }, []);
  // Pixel initialized globally in index.tsx
  const handleBuy = (link: string, comboName: string, price: number) => {
    try {
      ;
      (window as any).fbq('track', 'InitiateCheckout', {
        value: price,
        currency: 'ARS',
        content_name: comboName
      });
    } catch (e) {}
    window.location.href = link;
  };
  const scrollToCheckout = () => {
    const element = document.getElementById('pricing');
    if (element) element.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  return <div className="min-h-screen bg-black text-gray-300 font-sans selection:bg-yellow-500 selection:text-black overflow-x-hidden" data-id="element-65">
      <UrgencyBar data-id="element-66" />
      <PurchasePopup data-id="element-67" />

      {/* --- HERO SECTION --- */}
      <section className="relative pt-24 pb-16 px-4 md:pt-32 md:pb-24 overflow-hidden" data-id="element-68">
        {/* Background Gradients */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-yellow-500/10 blur-[120px] rounded-full pointer-events-none" data-id="element-69" />

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-70">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-71">
            <span className="inline-block py-1 px-3 rounded border border-yellow-500/30 bg-yellow-500/10 text-yellow-500 font-bold text-xs md:text-sm uppercase tracking-widest mb-6" data-id="element-72">
              Nadie te preparó para esto...
            </span>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-8 uppercase tracking-tight" data-id="element-73">
              Códigos Ocultos del <br data-id="element-74" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600" data-id="element-75">
                Comportamiento Humano
              </span>
            </h1>
          </motion.div>

          {/* Book image — NO motion wrapper to avoid LCP render delay */}
          <div className="relative mx-auto max-w-sm mb-10" data-id="element-76">
            <div className="absolute inset-0 bg-yellow-500/20 blur-2xl -z-10 rounded-full" data-id="element-77" />
            <img src="https://cdn.magicpatterns.com/uploads/uSmcroKTKZCXej782ZGJuq/ChatGPT_Image_23_feb_2026,_17_24_01_(1).png" alt="Códigos Ocultos Book Cover" width={512} height={768} sizes="(max-width: 400px) 100vw, 400px" decoding="sync" fetchPriority="high" className="w-full h-auto rounded-lg shadow-2xl border border-zinc-800" data-id="element-78" />
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="text-center" data-id="element-79">
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-80">
              El sistema estructurado para leer intenciones, detectar mentiras y
              blindarte contra la manipulación moderna. Dejá de adivinar, empezá
              a saber.
            </p>

            <div className="flex flex-col items-center gap-6" data-id="element-81">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={scrollToCheckout} className="bg-yellow-500 text-black font-black text-lg py-4 px-8 rounded-full shadow-[0_0_20px_rgba(234,179,8,0.4)] hover:shadow-[0_0_30px_rgba(234,179,8,0.6)] transition-all flex items-center gap-2 uppercase tracking-wide" data-id="element-82">
                Activar Acceso Inmediato{' '}
                <ArrowDown className="w-5 h-5 animate-bounce" data-id="element-83" />
              </motion.button>

              <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-84">
                <div className="flex -space-x-2" data-id="element-85">
                  {[1, 2, 3, 4].map(i => <div key={i} className="w-6 h-6 rounded-full bg-zinc-800 border border-black flex items-center justify-center text-[8px] text-white" data-id="element-86">
                      <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" data-id="element-87" />
                    </div>)}
                </div>
                <span data-id="element-88">+1,200 estudiantes activos</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- SALES LETTER --- */}
      <section className="py-12 px-4 bg-zinc-950" data-id="element-89">
        <div className="max-w-2xl mx-auto" data-id="element-90">
          <div className="prose prose-invert prose-lg prose-p:text-gray-300 prose-headings:font-black prose-headings:text-white prose-strong:text-white prose-strong:font-bold" data-id="element-91">
            <h2 className="text-3xl md:text-4xl text-center mb-12 uppercase tracking-tight" data-id="element-92">
              Hay códigos que la mayoría{' '}
              <span className="text-yellow-500" data-id="element-93">no conoce</span>
            </h2>

            <p data-id="element-94">
              Y no porque sean imposibles de entender. Sino porque nadie los
              explica de forma estructurada.
            </p>
            <p data-id="element-95">Cada conversación. Cada reunión. Cada interacción social.</p>
            <p className="font-bold text-white text-xl" data-id="element-96">
              Está llena de señales invisibles.
            </p>

            <ul className="list-none pl-0 space-y-2 my-8" data-id="element-97">
              {['Microincongruencias', 'Cambios de energía', 'Jerarquías implícitas', 'Pruebas de poder', 'Intentos de manipulación sutil'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-300" data-id="element-98">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-99" />
                  {item}
                </li>)}
            </ul>

            <p data-id="element-100">
              El problema no es que no existan.{' '}
              <strong data-id="element-101">El problema es que la mayoría no sabe leerlas.</strong>
            </p>

            <p data-id="element-102">Eso genera inseguridad. Y la inseguridad genera desventaja.</p>
            <p data-id="element-103">
              No es falta de inteligencia. <strong data-id="element-104">Es falta de sistema.</strong>
            </p>

            {/* THE DISCOVERY BOX */}
            <div className="bg-zinc-900 border border-zinc-800 p-8 rounded-xl mt-8 mb-0 shadow-xl relative overflow-hidden" data-id="element-105">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent" data-id="element-106" />
              <h3 className="text-2xl font-black text-white mb-4 flex items-center gap-2" data-id="element-107">
                <BookOpen className="text-yellow-500" data-id="element-108" />
                EL DESCUBRIMIENTO
              </h3>
              <p className="mb-4" data-id="element-109">
                El comportamiento humano sigue patrones. Tiene estructuras
                repetibles. Se manifiesta en microseñales observables.
              </p>
              <p className="font-bold text-white" data-id="element-110">
                Cuando aprendés a identificarlas, dejás de adivinar… y empezás a
                interpretar con claridad.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- POR QUÉ FUNCIONA (compact) --- */}
      <section className="py-12 px-4 bg-black border-t border-zinc-900" data-id="element-111">
        <div className="max-w-5xl mx-auto" data-id="element-112">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-8 uppercase text-center" data-id="element-113">
            Por qué este sistema funciona
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-114">
            {[{
            icon: <Brain className="w-8 h-8 text-yellow-500" data-id="element-115" />,
            title: 'Patrones reales',
            desc: 'Respuestas biológicas y psicológicas imposibles de ocultar.'
          }, {
            icon: <BookOpen className="w-8 h-8 text-yellow-500" data-id="element-116" />,
            title: 'Sistema progresivo',
            desc: 'De lo básico a lo avanzado: ver, interpretar e influir.'
          }, {
            icon: <Eye className="w-8 h-8 text-yellow-500" data-id="element-117" />,
            title: 'Aplicación inmediata',
            desc: 'Terminás un módulo y ya ves los patrones.'
          }].map((item, idx) => <div key={idx} className="flex items-start gap-4 bg-zinc-900/50 p-5 rounded-xl border border-zinc-800" data-id="element-118">
                <div className="shrink-0 mt-0.5" data-id="element-119">{item.icon}</div>
                <div data-id="element-120">
                  <h3 className="text-base font-bold text-white mb-1" data-id="element-121">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-snug" data-id="element-122">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* --- BENEFITS (compact) --- */}
      <section className="py-12 px-4 bg-zinc-950 border-t border-zinc-900" data-id="element-123">
        <div className="max-w-5xl mx-auto" data-id="element-124">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-6 uppercase text-center" data-id="element-125">
            Qué va a cambiar en vos
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-126">
            {BENEFITS.map((benefit, idx) => <div key={idx} className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 px-4 py-3 rounded-lg" data-id="element-127">
                <div className="shrink-0" data-id="element-128">{benefit.icon}</div>
                <span className="text-sm font-medium text-white" data-id="element-129">
                  {benefit.title}
                </span>
              </div>)}
          </div>
          <p className="mt-6 text-center text-gray-500 text-sm" data-id="element-130">
            "Esto no es motivación, es{' '}
            <span className="text-yellow-500 font-bold" data-id="element-131">
              entrenamiento técnico
            </span>
            ."
          </p>
        </div>
      </section>

      {/* --- BONUSES (compact) --- */}
      <section className="py-12 px-4 bg-zinc-950 border-t border-zinc-900" data-id="element-132">
        <div className="max-w-5xl mx-auto" data-id="element-133">
          <div className="flex items-center justify-center gap-3 mb-8" data-id="element-134">
            <span className="bg-red-600 text-white text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-widest animate-pulse" data-id="element-135">
              Gratis hoy
            </span>
            <h2 className="text-2xl md:text-3xl font-black text-white uppercase" data-id="element-136">
              Bonus Exclusivos
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-137">
            {BONUSES.map((bonus, idx) => <div key={idx} className="flex items-start gap-3 bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-id="element-138">
                <div className="shrink-0 w-9 h-9 rounded-lg bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center mt-0.5" data-id="element-139">
                  <Gift className="w-4.5 h-4.5 text-yellow-500" data-id="element-140" />
                </div>
                <div className="min-w-0" data-id="element-141">
                  <div className="flex items-center gap-2 mb-1" data-id="element-142">
                    <h3 className="text-sm font-bold text-white leading-tight truncate" data-id="element-143">
                      {bonus.title}
                    </h3>
                    <span className="bg-green-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0" data-id="element-144">
                      GRATIS
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs leading-snug" data-id="element-145">
                    {bonus.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* --- PRICING / CHECKOUT --- */}
      <section id="pricing" className="pt-8 pb-16 px-4 bg-black relative" data-id="element-146">
        <div className="max-w-3xl mx-auto" data-id="element-147">
          <div className="text-center mb-8" data-id="element-148">
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4 uppercase" data-id="element-149">
              Elige tu Nivel
            </h2>
            <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 px-3 py-1.5 rounded text-red-500 font-bold text-sm animate-pulse" data-id="element-150">
              <AlertTriangle className="w-3.5 h-3.5" data-id="element-151" />
              <span data-id="element-152">Solo quedan 47 copias disponibles</span>
            </div>
            <div className="inline-flex items-center gap-1.5 mt-4 bg-white rounded-full px-4 py-1.5" data-id="element-153">
              <Lock className="w-3 h-3 text-gray-400" data-id="element-154" />
              <span className="text-gray-500 text-[10px] font-bold uppercase tracking-wider" data-id="element-155">
                Pago seguro vía
              </span>
              <img src="https://cdn.magicpatterns.com/uploads/1bsRCVsp26iC5TeXgpfvCB/pasted-image.png" alt="Mercado Pago" width={80} height={26} loading="lazy" decoding="async" className="h-5 w-auto object-contain" data-id="element-156" />
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl" data-id="element-157">
            {/* Combo Options — compact rows */}
            <div className="flex flex-col" data-id="element-158">
              {COMBO_DATA.map((combo, idx) => {
              const isSelected = selectedCombo === idx;
              return <div key={idx} data-id="element-159">
                    <div onClick={() => setSelectedCombo(idx)} className={`relative px-5 py-4 cursor-pointer transition-all duration-200 border-b border-zinc-800 last:border-b-0 ${isSelected ? 'bg-zinc-800/50 border-l-4 border-l-yellow-500' : 'bg-transparent border-l-4 border-l-transparent hover:bg-zinc-800/30'}`} data-id="element-160">
                      {combo.isRecommended && <div className="absolute top-0 right-6 bg-yellow-500 text-black text-[9px] font-black px-2 py-0.5 rounded-b uppercase tracking-widest" data-id="element-161">
                          Más Vendido
                        </div>}

                      <div className="flex items-center gap-3" data-id="element-162">
                        {/* Radio */}
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'border-yellow-500 bg-yellow-500/20' : 'border-zinc-600 bg-zinc-950'}`} data-id="element-163">
                          {isSelected && <motion.div layoutId="radio-indicator" className="w-2.5 h-2.5 rounded-full bg-yellow-500" data-id="element-164" />}
                        </div>

                        {/* Title + Description */}
                        <div className="flex-1 min-w-0" data-id="element-165">
                          <h3 className={`text-base md:text-lg font-black uppercase leading-tight ${isSelected ? 'text-yellow-500' : 'text-white'}`} data-id="element-166">
                            {combo.title}
                          </h3>
                          <p className="text-gray-500 text-xs mt-0.5 truncate" data-id="element-167">
                            {combo.description}
                          </p>
                        </div>

                        {/* Price */}
                        <div className="flex flex-col items-end shrink-0" data-id="element-168">
                          <div className="flex items-center gap-1.5" data-id="element-169">
                            <span className="text-gray-500 line-through text-xs" data-id="element-170">
                              ${combo.originalPrice}
                            </span>
                            <span className="bg-red-600 text-white text-[9px] font-bold px-1 py-0.5 rounded leading-none" data-id="element-171">
                              {combo.discount}
                            </span>
                          </div>
                          <div className="flex items-baseline gap-0.5" data-id="element-172">
                            <span className="text-[10px] text-gray-400 font-bold" data-id="element-173">
                              ARS
                            </span>
                            <span className={`text-2xl md:text-3xl font-black tracking-tight ${isSelected ? 'text-white' : 'text-gray-400'}`} data-id="element-174">
                              {combo.price}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expandable features — only for selected combo */}
                    <AnimatePresence data-id="element-175">
                      {isSelected && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.25
                  }} className="overflow-hidden bg-zinc-800/30 border-b border-zinc-800 border-l-4 border-l-yellow-500" data-id="element-176">
                          <div className="px-5 py-3 pl-13" data-id="element-177">
                            <p className="text-[11px] text-yellow-500/80 font-bold uppercase tracking-wider mb-2" data-id="element-178">
                              Incluye:
                            </p>
                            <ul className="space-y-1.5" data-id="element-179">
                              {combo.features.map((feature, fIdx) => <li key={fIdx} className="flex items-start gap-2" data-id="element-180">
                                  <Check className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${feature.highlight ? 'text-yellow-400' : feature.bonus ? 'text-green-500' : 'text-gray-500'}`} data-id="element-181" />
                                  <span className={`text-xs ${feature.highlight ? 'text-yellow-400 font-bold' : 'text-gray-300'}`} data-id="element-182">
                                    {feature.text}
                                    {feature.bonus && <span className="text-green-500 font-bold ml-1" data-id="element-183">
                                        (Gratis)
                                      </span>}
                                  </span>
                                </li>)}
                            </ul>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
            })}
            </div>

            {/* CTA Area */}
            <div className="px-5 py-4 bg-zinc-950 border-t border-zinc-800" data-id="element-184">
              <button onClick={() => {
              const combo = COMBO_DATA[selectedCombo];
              handleBuy(combo.link, combo.comboName, combo.comboPrice);
            }} className="w-full py-4 rounded-xl font-black text-base uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-2 bg-yellow-500 text-black hover:bg-yellow-400 hover:shadow-[0_0_30px_rgba(234,179,8,0.4)]" data-id="element-185">
                COMPRAR AHORA
              </button>

              <div className="mt-3 flex flex-wrap justify-center items-center gap-x-4 gap-y-1 text-[11px] text-gray-500" data-id="element-186">
                <span className="flex items-center gap-1" data-id="element-187">
                  <Lock className="w-3 h-3 text-yellow-500" data-id="element-188" />
                  Pago 100% Seguro
                </span>
                <span data-id="element-189">✔ Acceso inmediato</span>
                <span data-id="element-190">✔ PDF por email</span>
                <span data-id="element-191">✔ Garantía 7 días</span>
              </div>
            </div>
          </div>

          {/* Trust strip */}
          <div className="mt-10 bg-zinc-900 border border-zinc-800 rounded-xl p-4 shadow-xl" data-id="element-192">
            <div className="flex flex-wrap justify-center gap-4 md:gap-8 text-xs text-gray-400 font-medium" data-id="element-193">
              <div className="flex items-center gap-1.5" data-id="element-194">
                <Check className="w-4 h-4 text-yellow-500" data-id="element-195" />
                Acceso inmediato
              </div>
              <div className="flex items-center gap-1.5" data-id="element-196">
                <Check className="w-4 h-4 text-yellow-500" data-id="element-197" />
                Material descargable
              </div>
              <div className="flex items-center gap-1.5" data-id="element-198">
                <Check className="w-4 h-4 text-yellow-500" data-id="element-199" />
                Pago seguro
              </div>
              <div className="flex items-center gap-1.5" data-id="element-200">
                <Check className="w-4 h-4 text-yellow-500" data-id="element-201" />
                Garantía 7 días
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- GARANTÍA SIN RIESGO (after checkout) --- */}
      <section className="py-16 px-4 bg-black border-t border-zinc-900" data-id="element-202">
        <div className="max-w-3xl mx-auto text-center" data-id="element-203">
          <Shield className="w-16 h-16 text-green-500 mx-auto mb-4" data-id="element-204" />
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase" data-id="element-205">
            7 Días de Garantía por Inconvenientes
          </h2>
          <p className="text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto" data-id="element-206">
            Si tenés algún inconveniente con la descarga, el acceso o la
            recepción del material, te lo resolvemos o te devolvemos el 100% de
            tu dinero. Sin complicaciones.
          </p>
        </div>
      </section>

      {/* --- QUÉ VAS A RECIBIR EXACTAMENTE (MOVED AFTER CHECKOUT) --- */}
      <section className="py-20 px-4 bg-black border-t border-zinc-900" data-id="element-207">
        <div className="max-w-4xl mx-auto" data-id="element-208">
          <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-12 uppercase" data-id="element-209">
            Qué vas a recibir exactamente
          </h2>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-zinc-900 border-l-4 border-yellow-500 p-8 rounded-r-xl shadow-xl" data-id="element-210">
            <ul className="space-y-6" data-id="element-211">
              <li className="flex items-center gap-4" data-id="element-212">
                <div className="bg-zinc-800 p-3 rounded-lg" data-id="element-213">
                  <Mail className="w-6 h-6 text-yellow-500" data-id="element-214" />
                </div>
                <span className="text-lg text-gray-200 font-medium" data-id="element-215">
                  📧 Acceso inmediato tras el pago
                </span>
              </li>
              <li className="flex items-center gap-4" data-id="element-216">
                <div className="bg-zinc-800 p-3 rounded-lg" data-id="element-217">
                  <BookOpen className="w-6 h-6 text-yellow-500" data-id="element-218" />
                </div>
                <span className="text-lg text-gray-200 font-medium" data-id="element-219">
                  📄 Manuales en formato PDF profesional
                </span>
              </li>
              <li className="flex items-center gap-4" data-id="element-220">
                <div className="bg-zinc-800 p-3 rounded-lg" data-id="element-221">
                  <Smartphone className="w-6 h-6 text-yellow-500" data-id="element-222" />
                </div>
                <span className="text-lg text-gray-200 font-medium" data-id="element-223">
                  📱 Compatible con celular, tablet y PC
                </span>
              </li>
              <li className="flex items-center gap-4" data-id="element-224">
                <div className="bg-zinc-800 p-3 rounded-lg" data-id="element-225">
                  <RefreshCw className="w-6 h-6 text-yellow-500" data-id="element-226" />
                </div>
                <span className="text-lg text-gray-200 font-medium" data-id="element-227">
                  🔄 Actualizaciones futuras sin costo
                </span>
              </li>
              <li className="flex items-center gap-4" data-id="element-228">
                <div className="bg-zinc-800 p-3 rounded-lg" data-id="element-229">
                  <Infinity className="w-6 h-6 text-yellow-500" data-id="element-230" />
                </div>
                <span className="text-lg text-gray-200 font-medium" data-id="element-231">
                  ♾️ Acceso de por vida — sin suscripciones
                </span>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* --- TESTIMONIALS (MOVED AFTER CHECKOUT) --- */}
      <section className="py-20 px-4 bg-zinc-950 border-t border-zinc-900" data-id="element-232">
        <div className="max-w-6xl mx-auto" data-id="element-233">
          <h2 className="text-3xl md:text-4xl font-black text-white text-center mb-16 uppercase" data-id="element-234">
            Lo que dicen los estudiantes
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-235">
            {TESTIMONIALS.map((testimonial, idx) => <TestimonialCard key={idx} {...testimonial} data-id="element-236" />)}
          </div>
        </div>
      </section>

      {/* --- NEW: PREGUNTAS FRECUENTES --- */}
      <section className="py-20 px-4 bg-zinc-950 border-t border-zinc-900" data-id="element-237">
        <div className="max-w-3xl mx-auto" data-id="element-238">
          <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-12 uppercase" data-id="element-239">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-4" data-id="element-240">
            {FAQS.map((faq, i) => <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden" data-id="element-241">
                <button onClick={() => toggleFaq(i)} className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none hover:bg-zinc-800/50 transition-colors" data-id="element-242">
                  <span className="font-bold text-white text-lg" data-id="element-243">{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="w-5 h-5 text-yellow-500" data-id="element-244" /> : <ChevronDown className="w-5 h-5 text-gray-500" data-id="element-245" />}
                </button>
                <AnimatePresence data-id="element-246">
                  {openFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="px-6 pb-4 text-gray-400" data-id="element-247">
                      {faq.a}
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* --- NEW: CIERRE PSICOLÓGICO --- */}
      <section className="py-24 px-4 bg-black relative overflow-hidden border-t border-zinc-900" data-id="element-248">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-yellow-500/5 blur-[100px] rounded-full pointer-events-none" data-id="element-249" />
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-250">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-251">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 uppercase leading-tight" data-id="element-252">
              Cada conversación que tenés sin este sistema es una oportunidad
              perdida.
            </h2>
            <p className="text-xl text-gray-400 mb-10" data-id="element-253">
              Las señales están ahí. La pregunta es: ¿vas a seguir ignorándolas?
            </p>
            <button onClick={scrollToCheckout} className="bg-yellow-500 text-black font-black text-xl py-5 px-10 rounded-full shadow-[0_0_20px_rgba(234,179,8,0.4)] hover:shadow-[0_0_30px_rgba(234,179,8,0.6)] transition-all uppercase tracking-wide mb-6" data-id="element-254">
              Activar Mi Acceso Ahora
            </button>
            <div className="flex justify-center items-center gap-4 text-sm text-gray-500 font-medium" data-id="element-255">
              <span data-id="element-256">✔ Acceso inmediato</span>
              <span data-id="element-257">·</span>
              <span data-id="element-258">✔ Pago seguro</span>
              <span data-id="element-259">·</span>
              <span data-id="element-260">✔ Garantía 7 días</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="py-12 bg-black border-t border-zinc-900 text-center" data-id="element-261">
        <div className="max-w-4xl mx-auto px-4" data-id="element-262">
          <h3 className="text-xl font-black text-white mb-4 uppercase" data-id="element-263">
            Códigos Ocultos
          </h3>
          <p className="text-gray-500 text-sm mb-8" data-id="element-264">
            Este producto no garantiza la obtención de resultados. Cualquier
            referencia al desempeño de una estrategia no debe ser interpretada
            como una garantía de resultados.
          </p>
          <div className="flex justify-center gap-6 text-sm text-gray-600" data-id="element-265">
            <a href="#" className="hover:text-yellow-500 transition-colors" data-id="element-266">
              Términos y Condiciones
            </a>
            <a href="#" className="hover:text-yellow-500 transition-colors" data-id="element-267">
              Política de Privacidad
            </a>
            <a href="#" className="hover:text-yellow-500 transition-colors" data-id="element-268">
              Contacto
            </a>
          </div>
          <p className="mt-8 text-xs text-zinc-800" data-id="element-269">
            © 2024 Códigos Ocultos. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}